import PageHeader from "@/components/PageHeader";
import { Recycle, TrendingDown, DollarSign, Leaf, RotateCcw } from "lucide-react";

const SUS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8f287472-b53b-4254-a417-af9039f0ca84/images/6a1a39081052676dcfc9d7703f167bda22bf5dcd12cc97b54b9a6cf1830c53cf.png";

const benefits = [
  { icon: TrendingDown, t: "Reduced Waste", d: "Diverting electronics from landfills by giving them a second life." },
  { icon: RotateCcw, t: "Extended Product Life", d: "Hardware put back into productive use rather than discarded." },
  { icon: DollarSign, t: "Affordable Electronics", d: "Quality products at lower prices for budget-conscious buyers." },
  { icon: Recycle, t: "Responsible Reuse", d: "Inspected and graded resale of returned, surplus, and overstock items." },
];

export default function Sustainability() {
  return (
    <div data-testid="sustainability-page">
      <PageHeader
        eyebrow="Sustainability"
        breadcrumb="Sustainability"
        title="Giving electronics a second life."
        subtitle="By supporting refurbishment and responsible resale, customers help reduce electronic waste and promote a more sustainable technology ecosystem."
      />

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#16A34A]">
              Our Commitment
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Less waste. More value. Better hardware decisions.
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-gray-700">
              Every refurbished, open-box, or customer-return product we resell
              keeps perfectly functional electronics out of landfills. Our
              process — sourcing, inspection, testing, grading, and
              transparent resale — is the practical version of a circular
              economy for consumer tech.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-gray-700">
              We believe extending the life of electronic devices is one of
              the most direct, measurable ways anyone can contribute to a more
              sustainable technology ecosystem.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-3 border border-gray-200 rounded-sm pointer-events-none" />
              <img
                src={SUS_IMG}
                alt="Laptop and plant representing sustainable technology reuse"
                className="relative w-full h-[420px] lg:h-[480px] object-cover rounded-sm border border-gray-300"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F9FAFB] border-y border-gray-200">
        <div className="cnl-container py-20 lg:py-24">
          <div className="max-w-2xl mb-12">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
              The Benefits
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Why refurbishment and resale matter.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((b) => (
              <div
                key={b.t}
                className="bg-white border border-gray-200 rounded-sm p-7 hover:border-[#16A34A] transition-colors"
                data-testid={`sustainability-benefit-${b.t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#16A34A]/10 border border-[#16A34A]/30 text-[#16A34A]">
                  <b.icon size={20} />
                </div>
                <h3 className="mt-5 text-[17px] font-semibold tracking-tight text-[#0B192C]">
                  {b.t}
                </h3>
                <p className="mt-2 text-[13.5px] text-gray-600 leading-relaxed">
                  {b.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B192C] text-white cnl-grain">
        <div className="cnl-container py-20 lg:py-24 grid md:grid-cols-3 gap-10 items-center">
          <div className="md:col-span-2">
            <Leaf size={32} className="text-[#16A34A]" />
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              A circular approach to consumer electronics.
            </h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-gray-300 max-w-2xl">
              Sourcing returns and overstock, testing what we can, grading
              transparently, and reselling at fair prices — that’s our
              sustainability practice in action. Simple, practical, and
              repeatable.
            </p>
          </div>
          <div className="border border-[#1E3E62] rounded-sm p-7">
            <div className="text-[12px] font-semibold uppercase tracking-[0.2em] text-blue-300">
              Established
            </div>
            <div className="mt-2 mono text-5xl font-bold text-white">2020</div>
            <p className="mt-3 text-[13.5px] text-gray-300">
              Eight years of giving hardware a second life from Corona, California.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
